// ANA-R3-GEOM-05 — the on-tile zoomable polygon editor.
//
// Mounted over a finding tile's <canvas> (revealed on hover/focus). It reuses
// the shared `PolygonEditor` primitive (GEOM-02) — the SAME seam GEOM-03/04 use
// in the Viewer — but supplies a CANVAS CoordinateTransform built from TILE-03's
// crop numbers (`tileTransform.ts`) instead of an OSD viewport. A local
// mousewheel-zoom + pan mini-viewport sits on top so editing on a small (270px)
// tile is precise; handles stay constant SCREEN px (the editor sizes them so).
//
// Seeding (story §1):
//   * Initial `{ mode, points }` come from the finding's effective geometry —
//     the session override if one exists, else the finding's `path` (or its
//     bbox corners for a legacy null-path row), via `seedFromGeometry`
//     (reused from editGeometryStore). The mode is detected from the shape
//     (`detectMode`): axis-aligned quad ⇒ rect-resize, other closed ⇒ polygon,
//     open ⇒ polyline.
//   * Convert (rect ↔ polygon ↔ polyline) is host-side via toRect/toPolygon/
//     toPolyline (AC4).
//
// Commit (story §2 + ANA-R4-TILE-03): there is NO explicit Save button — the
// draft is serialised to a NORMALISED flat path (`serialisePoints`) and written
// to the session override store when the editor LEAVES (mouse-leave / blur →
// unmount). Nothing is PATCHed and no network runs at edit time (immutability —
// AC2/AC5). Reset discards the draft AND any existing override (back to the DB
// geometry) and skips the leave-commit.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Stack, Tooltip } from "@mui/material";
import {
  PolygonEditor,
  isSelfIntersecting,
  serialisePoints,
  toPolygon,
  toPolyline,
  toRect,
  type EditorMode,
  type PolygonValue,
} from "@aeriosiq/ui";
import type { AiFinding } from "@aeriosiq/types";
import { computeTileCrop } from "../components/tileCrop.js";
import { seedFromGeometry } from "../state/editGeometryStore.js";
import {
  useTileGeometryOverrideStore,
  type TileGeometryOverride,
} from "./tileGeometryOverrideStore.js";
import {
  applyTileWheelZoom,
  clampEditZoom,
  createTileTransform,
  editZoomMaxForCap,
  type TileCropFrame,
  type TileLocalView,
} from "./tileTransform.js";
import { RotateCcwIcon } from "@aeriosiq/ui/icons";

export interface TileGeometryEditorProps {
  /** The finding being edited (its `path`/`bbox` seed the initial shape). */
  finding: AiFinding;
  /** Source image pixel dims (normalised ↔ source-px conversion). */
  imgW: number;
  imgH: number;
  /** Logical tile display side in CSS px (the chosen TILE-03 preset). */
  tileDisplayPx: number;
  /** Max-zoom cap (% native) — drives the SAME crop the canvas drew. */
  maxZoomPercent: number;
  /**
   * ANA-R4-TILE-02 — the local zoom/pan is now OWNED BY THE TILE (controlled),
   * so the image canvas and the editor geometry zoom/pan together. The editor
   * reads `view` and reports changes via `onViewChange`; wheel-zoom is capped so
   * the effective magnification never exceeds `maxZoomPercent` (the Tiles
   * setting) — a user cannot zoom in further than the cap.
   */
  view: TileLocalView;
  onViewChange: (updater: (v: TileLocalView) => TileLocalView) => void;
  /** Close the editor (the host hides it; e.g. blur, Done). */
  onClose: () => void;
}

/**
 * Re-derive the tile crop frame the canvas used. The override (if any) reframes
 * the crop around the MODIFIED geometry's bbox (story §2 — crop framing follows
 * the override); otherwise it follows the finding's bbox. Both feed the SAME
 * `computeTileCrop` the canvas uses, so the editor's transform lines up
 * pixel-for-pixel with what the user sees.
 */
function useCropFrame(
  finding: AiFinding,
  override: TileGeometryOverride | null,
  imgW: number,
  imgH: number,
  tileDisplayPx: number,
  maxZoomPercent: number,
): TileCropFrame | null {
  return useMemo(() => {
    const bbox = bboxForFraming(finding, override);
    if (!bbox || imgW <= 0 || imgH <= 0) return null;
    const { cropSide, sx, sy } = computeTileCrop({
      bbox,
      imgW,
      imgH,
      tileDisplayPx,
      maxZoomPercent,
    });
    return { cropSide, sx, sy, imgW, imgH, tileDisplayPx };
  }, [finding, override, imgW, imgH, tileDisplayPx, maxZoomPercent]);
}

/** Normalised bbox for crop framing: the override's AABB, else the finding's. */
function bboxForFraming(
  finding: AiFinding,
  override: TileGeometryOverride | null,
): { x: number; y: number; w: number; h: number } | null {
  if (override) {
    const aabb = pathAabb(override.path);
    if (aabb) return aabb;
  }
  const bb = finding.bbox;
  if (bb) return { x: bb.x, y: bb.y, w: bb.w, h: bb.h };
  return null;
}

/** AABB (normalised) of a flat `[x0,y0,…]` path, or null if degenerate. */
function pathAabb(
  flat: number[],
): { x: number; y: number; w: number; h: number } | null {
  if (flat.length < 4) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < flat.length; i += 2) {
    const x = flat[i];
    const y = flat[i + 1];
    if (typeof x !== "number" || typeof y !== "number") continue;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  if (!Number.isFinite(minX)) return null;
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function TileGeometryEditor({
  finding,
  imgW,
  imgH,
  tileDisplayPx,
  maxZoomPercent,
  view,
  onViewChange,
  onClose,
}: TileGeometryEditorProps): React.ReactElement | null {
  const setOverride = useTileGeometryOverrideStore((s) => s.set);
  const clearOverride = useTileGeometryOverrideStore((s) => s.clear);
  const override = useTileGeometryOverrideStore(
    (s) => s.overrides[finding.id] ?? null,
  );

  // Seed the draft once on mount from the EFFECTIVE geometry (override → path →
  // bbox). The override seeds back into its own mode (AC4: a converted shape
  // re-seeds in the mode it was committed in).
  const seed = useMemo<PolygonValue>(() => {
    if (override) {
      const s = seedFromGeometry(override.path, null);
      // Honour the stored mode (e.g. a non-axis quad the analyst dragged from a
      // rect stays `polygon`); detectMode handles the rest.
      return { mode: override.mode, points: s.points };
    }
    return seedFromGeometry(finding.path, finding.bbox);
    // Seed only when the finding's geometry / its override changes — vertex
    // drags drive `draft` below, not a re-seed.
  }, [finding.path, finding.bbox, override]);

  const [draft, setDraft] = useState<PolygonValue>(seed);
  // Re-seed the draft whenever the seed identity changes (tile reuse / a fresh
  // override). Vertex drags update `draft` only; they do not re-seed.
  useEffect(() => {
    setDraft(seed);
  }, [seed]);

  // ANA-R4-TILE-02 — the local mini-viewport (mousewheel zoom + pan) is now
  // CONTROLLED by the tile (`view` prop / `onViewChange`) so the image canvas and
  // the editor geometry share one transform and zoom together.
  const frame = useCropFrame(
    finding,
    override,
    imgW,
    imgH,
    tileDisplayPx,
    maxZoomPercent,
  );

  const transform = useMemo(() => {
    if (!frame) return null;
    return createTileTransform(frame, view);
  }, [frame, view]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Mousewheel → local zoom about the cursor (AC1). We attach a non-passive
  // listener so we can preventDefault (stop the page scrolling) on the tile.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || !frame) return;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      const rect = node.getBoundingClientRect();
      const fx = e.clientX - rect.left;
      const fy = e.clientY - rect.top;
      // ANA-R4-TILE-02 — cap zoom-in at the Tiles max-zoom setting.
      const cap = editZoomMaxForCap(frame, maxZoomPercent);
      onViewChange((v) => {
        const next = applyTileWheelZoom(v, frame, e.deltaY, fx, fy, cap);
        return { ...next, version: v.version + 1 };
      });
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", onWheel);
    };
  }, [frame, maxZoomPercent, onViewChange]);

  // Middle / right mouse drag → pan the mini-viewport (left = vertex edit).
  const panRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const onMouseDownCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.button === 0) return; // left = editor
      e.preventDefault();
      e.stopPropagation();
      panRef.current = { x: e.clientX, y: e.clientY, zoom: view.editZoom };
    },
    [view.editZoom],
  );
  const onMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      const start = panRef.current;
      if (!start) return;
      const z = clampEditZoom(start.zoom);
      // Screen delta → base-tile delta is screen/z (pan lives in base px).
      const dx = (e.clientX - start.x) / z;
      const dy = (e.clientY - start.y) / z;
      panRef.current = { x: e.clientX, y: e.clientY, zoom: start.zoom };
      onViewChange((v) => ({
        ...v,
        panX: v.panX + dx,
        panY: v.panY + dy,
        version: v.version + 1,
      }));
    },
    [onViewChange],
  );
  const endPan = useCallback((): void => {
    panRef.current = null;
  }, []);

  const handleChange = useCallback((next: PolygonValue) => {
    setDraft(next);
  }, []);

  const convert = useCallback((target: EditorMode): void => {
    setDraft((cur) => {
      if (target === "rect") return toRect(cur);
      if (target === "polygon") return toPolygon(cur);
      return toPolyline(cur);
    });
  }, []);

  // ANA-R4-TILE-03 — commit-on-leave. The explicit ✓ Save button is gone; the
  // edit is written to the session override when the editor LEAVES (mouse-leave /
  // blur → unmount), i.e. "moving out of the tile is the same event as Save"
  // (Christian 2026-06-11). No network at edit time (immutability — GEOM-05).
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const seedRef = useRef(seed);
  seedRef.current = seed;
  // Set before a Reset's onClose so the unmount-commit doesn't re-write the
  // just-discarded draft.
  const skipCommitRef = useRef(false);

  useEffect(() => {
    return () => {
      if (skipCommitRef.current) return;
      const d = draftRef.current;
      const min = d.mode === "polyline" ? 2 : 3;
      if (d.points.length < min) return;
      // Never persist a self-intersecting shape (VALID-01) — keep the original.
      if (isSelfIntersecting(d.points, d.mode !== "polyline")) return;
      const normFlat = serialisePoints(d);
      if (normFlat.length < 4) return;
      // ANA-R4-PROMOTE-01 — never store a non-finite override (a NaN/Infinity
      // vertex would 400 the bulk-promote later); keep the original geometry.
      if (!normFlat.every((n) => Number.isFinite(n))) return;
      // Only write an override if the geometry actually changed from the seed —
      // a hover-without-edit must not create a spurious "edited" override.
      const seedFlat = serialisePoints(seedRef.current);
      if (
        d.mode === seedRef.current.mode &&
        normFlat.length === seedFlat.length &&
        normFlat.every((n, i) => Math.abs(n - (seedFlat[i] ?? 0)) < 1e-6)
      ) {
        return;
      }
      setOverride(finding.id, { path: normFlat, mode: d.mode });
    };
    // Stable deps: the cleanup runs on unmount (leave) and reads the latest
    // draft/seed via refs. finding.id change (tile reuse) commits the old first.
  }, [finding.id, setOverride]);

  // Reset-to-original (story §2) — discard the override + the draft, and skip the
  // unmount-commit so the cleared geometry isn't immediately re-written.
  const handleReset = useCallback((): void => {
    skipCommitRef.current = true;
    clearOverride(finding.id);
    onClose();
  }, [clearOverride, finding.id, onClose]);

  const onContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
    },
    [],
  );

  if (!frame || !transform) return null;

  return (
    <Box
      ref={wrapperRef}
      data-testid="tile-geometry-editor"
      data-edit-mode={draft.mode}
      data-edit-zoom={String(Math.round(view.editZoom * 100))}
      role="presentation"
      onMouseDownCapture={onMouseDownCapture}
      onMouseMove={onMouseMove}
      onMouseUp={endPan}
      onMouseLeave={endPan}
      onContextMenu={onContextMenu}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 6,
        cursor: "crosshair",
      }}
    >
      <PolygonEditor
        value={draft}
        onChange={handleChange}
        transform={transform}
        width={tileDisplayPx}
        height={tileDisplayPx}
        editable
        ariaLabel={`Edit finding geometry (${draft.mode})`}
      />
      {/* On-tile toolbar — Save / Reset + mode-convert. Compact so it fits the
          smallest (270px) tile (OQ2: editable on all sizes). */}
      <Stack
        direction="row"
        spacing={0.25}
        data-testid="tile-geometry-toolbar"
        onMouseDownCapture={(e) => {
          // Toolbar clicks must NOT start a pan or reach the editor surface.
          e.stopPropagation();
        }}
        onClick={(e) => {
          // …and must NOT bubble up to the tile's select handler (a Save /
          // Reset / convert is an edit action, not a tile selection).
          e.stopPropagation();
        }}
        sx={{
          position: "absolute",
          left: 4,
          top: 4,
          p: 0.25,
          borderRadius: 0.75,
          backgroundColor: "background.paper",
          boxShadow: 2,
          alignItems: "center",
        }}
      >
        <Tooltip title="Reset to the original AI geometry">
          <Button
            size="small"
            variant="text"
            color="inherit"
            onClick={handleReset}
            data-testid="tile-geometry-reset"
            aria-label="Reset tile geometry to original"
            sx={{ minWidth: 0, px: 0.5, py: 0.25 }}
          >
            <RotateCcwIcon fontSize="small" />
          </Button>
        </Tooltip>
        {draft.mode !== "polygon" ? (
          <Tooltip title="Convert to free-form polygon">
            <Button
              size="small"
              variant="text"
              onClick={() => {
                convert("polygon");
              }}
              data-testid="tile-geometry-to-polygon"
              sx={{ minWidth: 0, px: 0.5, py: 0.25, fontSize: 11 }}
            >
              Poly
            </Button>
          </Tooltip>
        ) : null}
        {draft.mode !== "rect" ? (
          <Tooltip title="Convert to rectangle">
            <Button
              size="small"
              variant="text"
              onClick={() => {
                convert("rect");
              }}
              data-testid="tile-geometry-to-rect"
              sx={{ minWidth: 0, px: 0.5, py: 0.25, fontSize: 11 }}
            >
              Rect
            </Button>
          </Tooltip>
        ) : null}
      </Stack>
    </Box>
  );
}
